/**
 * Video (§2.2): 16:9, radius 14, "Recorded · {location}" badge top-left, the control bar at the bottom.
 * The Vimeo player renders the picture; the design's controls drive it (vimeoClock.ts). The time
 * readout follows the store's currentTime by direct DOM writes, never React state.
 *
 * Another city's recording (meeting.player, worker/src/city-meetings.js) is its video site's file, played in a <video>
 * that the same controls and clock drive (vimeoClock.ts attachVideo); a recording without a file plays in its video
 * site's own player (an embed with its own controls; the transcript and timeline then can't move it).
 *
 * The player lives for the whole session, not the page: PlayerHost (rendered once by the Shell) owns the iframe,
 * and the meeting page only marks where it goes (VideoStage, an empty 16:9 slot). An iframe that's moved or
 * re-created reloads and stops, so the player is never moved in the DOM; it's drawn over the slot instead.
 *
 * Mini player (spec 06 card 4): when the slot scrolls out of view, or you open another page, while the video plays,
 * the player docks (320×180, 24px inset) and keeps playing with captions. Drag it anywhere in the window. Clicking it
 * takes you back to the video (back to the meeting page from any other page); ✕ closes it (pauses) and leaves you
 * where you are; it docks again only after the video has been back in view. The move is a FLIP with the motion
 * spring (recipe 10), a 100 ms fade under reduced motion.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { Captions, Maximize, Maximize2, PanelRightOpen, Pause, Play, Settings, Volume2, VolumeX, X } from "lucide-react";
import { prefersReducedMotion, recipes, springEasing, reducedFallback } from "../../motion";
import { meetingStore, useMeeting } from "../../stores/meetingStore";
import { useStoreEffect } from "../../hooks/useStoreEffect";
import { clock } from "../../lib/format";
import { navigate } from "../../app/router";
import { attachVideo, attachVimeo, isPlaying, pause, requestFullscreen, scrubTo, setMuted, togglePlay } from "./vimeoClock";
import { useScrub } from "./useScrub";
import { meetingHref } from "../../app/viewer";

// ---------- where the player goes: the meeting page's slot ----------
let slot: { el: HTMLElement; duration: number } | null = null;
const slotListeners = new Set<() => void>();
const setSlot = (s: typeof slot) => { slot = s; slotListeners.forEach((l) => l()); };
const useSlot = () => useSyncExternalStore((l) => { slotListeners.add(l); return () => { slotListeners.delete(l); }; }, () => slot, () => null);

/** The meeting page's place for the video. The player (PlayerHost) is drawn over it. */
export function VideoStage({ duration }: { duration: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const s = { el: ref.current!, duration };
    setSlot(s);
    return () => { if (slot === s) setSlot(null); };
  }, [duration]);
  return <div className="video-slot" ref={ref} />;
}

/** The sentence being spoken: with word timings, the words from the last sentence end through the next. */
function captionText(segId: string | null, w: number): string {
  const seg = segId ? meetingStore.getState().segments.find((x) => x.id === segId) : null;
  if (!seg) return "";
  if (!seg.words || w < 0) return seg.text;
  const ends = (k: number) => /[.?!]["”’)]*$/.test(seg.words![k].w);
  let a = w; while (a > 0 && !ends(a - 1)) a--;
  let b = w; while (b < seg.words.length - 1 && !ends(b)) b++;
  return seg.words.slice(a, b + 1).map((x) => x.w).join(" ");
}

/** Captions on the video (spec 05 card 5): the sentence being spoken, written to the DOM directly. */
function CaptionOverlay() {
  const ref = useRef<HTMLParagraphElement>(null);
  useStoreEffect(meetingStore, (s) => [s.activeSegmentId, s.activeWordIndex] as const, ([id, w]) => {
    const text = captionText(id, w);
    if (ref.current && ref.current.textContent !== text) { ref.current.textContent = text; ref.current.hidden = !text; }
  }, { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] });
  return <p className="video-captions" ref={ref} aria-live="off" />;
}

function usePlaying() {
  const [playing, setPlaying] = useState(isPlaying);
  useEffect(() => {
    // playing state is low-frequency (play/pause), so a small isolated component may re-render on it
    const onPlay = () => setPlaying(true), onPause = () => setPlaying(false);
    addEventListener("meeting:play", onPlay); addEventListener("meeting:pause", onPause);
    return () => { removeEventListener("meeting:play", onPlay); removeEventListener("meeting:pause", onPause); };
  }, []);
  return playing;
}

/** A bar you can click or drag to move through the video; its fill follows the playhead (DOM writes). */
function SeekBar({ duration, className, label }: { duration: number; className: string; label: string }) {
  const barRef = useRef<HTMLSpanElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  useScrub(barRef, duration);
  useStoreEffect(meetingStore, (s) => s.currentTime, (t) => {
    if (fillRef.current) fillRef.current.style.transform = `scaleX(${duration ? Math.min(1, t / duration) : 0})`;
  });
  useStoreEffect(meetingStore, (s) => Math.floor(s.currentTime), (t) => {
    barRef.current?.setAttribute("aria-valuenow", String(Math.min(t, duration)));
    barRef.current?.setAttribute("aria-valuetext", clock(Math.min(t, duration)));
  });
  const key = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 60 : 10, t = meetingStore.getState().currentTime;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      scrubTo(Math.min(duration, Math.max(0, t + (e.key === "ArrowRight" ? step : -step))), true);
    }
  };
  return (
    <span ref={barRef} className={className} role="slider" tabIndex={0} aria-label={label} aria-valuemin={0} aria-valuemax={duration}
      onClick={(e) => e.stopPropagation()} onKeyDown={key}>
      <span ref={fillRef} className={`${className}-fill`} />
    </span>
  );
}

/** The mini player's bottom row: play/pause, progress (drag to seek), time. */
function MiniControls({ duration }: { duration: number }) {
  const playing = usePlaying();
  const timeRef = useRef<HTMLSpanElement>(null);
  useStoreEffect(meetingStore, (s) => Math.floor(s.currentTime), (t) => { if (timeRef.current) timeRef.current.textContent = clock(t); });
  return (
    <div className="mini-controls" onPointerDown={(e) => e.stopPropagation()}>
      <button aria-label={playing ? "Pause" : "Play"} onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
        {playing ? <Pause size={13} fill="currentColor" strokeWidth={0} /> : <Play size={13} fill="currentColor" strokeWidth={0} />}
      </button>
      <SeekBar duration={duration} className="mini-progress" label="Seek" />
      <span ref={timeRef} className="mini-time" />
    </div>
  );
}

function PlayButton() {
  const playing = usePlaying();
  return (
    <button aria-label={playing ? "Pause" : "Play"} onClick={togglePlay}>
      {playing ? <Pause size={16} fill="currentColor" strokeWidth={0} /> : <Play size={16} fill="currentColor" strokeWidth={0} />}
    </button>
  );
}

type Mode = "page" | "mini" | "hidden";
const MINI = recipes.miniPlayerDock.size, INSET = recipes.miniPlayerDock.inset;
const clampPos = (p: { x: number; y: number }) => ({
  x: Math.min(Math.max(8, p.x), innerWidth - MINI.w - 8),
  y: Math.min(Math.max(8, p.y), innerHeight - MINI.h - 8),
});

/** The one video player, for the whole session (the Shell renders it). Nothing until a meeting has been opened. */
export function PlayerHost() {
  const meeting = useMeeting((s) => s.meeting);
  const here = useSlot();
  const playing = usePlaying();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const fileRef = useRef<HTMLVideoElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const [muted, setMutedState] = useState(false);
  const [captions, setCaptionsState] = useState(false);
  const hidden = useMeeting((s) => s.transcriptHidden);
  const [inView, setInView] = useState(true);
  const [docked, setDocked] = useState(false);
  const [closed, setClosed] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);   // where the mini player was dragged to
  const first = useRef<DOMRect | null>(null);   // FLIP: the rect before docking/undocking
  const duration = here?.duration ?? meeting?.video?.duration ?? Math.max(1, ...meetingStore.getState().segments.map((s) => s.end));

  // Hide / show transcript by keyboard: the control that was used goes away, so focus moves to its counterpart (§7).
  const showBtn = useRef<HTMLButtonElement>(null);
  const wasHidden = useRef(hidden);
  useEffect(() => {
    if (wasHidden.current === hidden) return;
    wasHidden.current = hidden;
    const lost = document.activeElement === document.body || !!document.activeElement?.closest(".tx-panel, .video-transcript-btn");
    if (!lost) return;
    if (hidden) showBtn.current?.focus();
    else requestAnimationFrame(() => document.querySelector<HTMLElement>('.tx-panel button[aria-label="Hide transcript"]')?.focus());
  }, [hidden]);

  // Is the meeting page's slot on screen?
  useEffect(() => {
    if (!here) { setInView(false); return; }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting));
    io.observe(here.el);
    return () => io.disconnect();
  }, [here]);

  // Dock while playing with the slot out of view (or on another page); undock when the slot is back in view.
  // A docked player that's paused stays docked (its play button resumes it).
  useEffect(() => {
    const want = here && inView ? false : playing && !closed ? true : docked;
    if (here && inView && closed) setClosed(false);
    if (want !== docked) { first.current = videoRef.current?.getBoundingClientRect() ?? null; setDocked(want); }
  }, [here, inView, playing, closed, docked]);

  const mode: Mode = docked ? "mini" : here ? "page" : "hidden";

  // The player sits in a fixed layer above the page, so the wheel over it would scroll nothing: pass it on to the
  // page's scroller (the meeting page's .panel, or the window), so the page scrolls as if the video weren't there.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || mode === "hidden") return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;   // pinch zoom
      const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
      const panel = here?.el.closest<HTMLElement>(".panel") ?? document.querySelector<HTMLElement>(".main > .panel");
      const scroller = panel && getComputedStyle(panel).overflowY !== "visible" ? panel : null;
      (scroller ?? window).scrollBy({ top: e.deltaY * k, left: e.deltaX * k, behavior: "instant" as ScrollBehavior });
    };
    el.addEventListener("wheel", onWheel, { passive: true });
    return () => el.removeEventListener("wheel", onWheel);
  }, [mode, here]);

  // Page mode: follow the slot (it scrolls with the page), clipped to what's visible of the page's scroller.
  useLayoutEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (mode !== "page" || !here) { el.style.clipPath = ""; return; }
    let raf = 0;
    const follow = () => {
      const r = here.el.getBoundingClientRect();
      const scroller = here.el.closest(".panel");
      const c = scroller && getComputedStyle(scroller).overflowY !== "visible" ? scroller.getBoundingClientRect() : { top: 0, left: 0, bottom: innerHeight, right: innerWidth };
      Object.assign(el.style, { top: `${r.top}px`, left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px` });
      const t = Math.max(0, c.top - r.top), b = Math.max(0, r.bottom - c.bottom), l = Math.max(0, c.left - r.left), rr = Math.max(0, r.right - c.right);
      el.style.clipPath = t || b || l || rr ? `inset(${t}px ${rr}px ${b}px ${l}px round var(--radius-xl))` : "";
      raf = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(raf);
  }, [mode, here]);

  // Mini mode: at its dragged position, else bottom-right.
  useLayoutEffect(() => {
    const el = videoRef.current;
    if (!el || mode !== "mini") return;
    // Default: bottom-right, above the phone's bottom tab bar.
    const bottom = matchMedia("(max-width: 767px)").matches ? INSET + 84 : INSET;
    const p = pos ? clampPos(pos) : clampPos({ x: innerWidth - MINI.w - INSET, y: innerHeight - MINI.h - bottom });
    Object.assign(el.style, { top: `${p.y}px`, left: `${p.x}px`, width: `${MINI.w}px`, height: `${MINI.h}px` });
  }, [mode, pos]);
  useEffect(() => {
    if (mode !== "mini" || !pos) return;
    const keep = () => setPos((p) => (p ? clampPos(p) : p));
    addEventListener("resize", keep);
    return () => removeEventListener("resize", keep);
  }, [mode, pos]);

  // FLIP between the page and the mini player.
  useLayoutEffect(() => {
    const el = videoRef.current, from = first.current;
    first.current = null;
    if (!el || !from || mode === "hidden") return;
    if (prefersReducedMotion()) { el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: reducedFallback.duration }); return; }
    const to = el.getBoundingClientRect();
    if (!to.width || !from.width) return;
    const { easing, duration: ms } = springEasing(recipes.miniPlayerDock.spring);
    el.animate([
      { transformOrigin: "top left", transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})` },
      { transformOrigin: "top left", transform: "none" },
    ], { duration: ms, easing });
  }, [docked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!frameRef.current || !meeting?.vimeoId) return;
    return attachVimeo(frameRef.current);
  }, [meeting?.vimeoId]);
  const file = meeting?.player?.kind === "video" ? meeting.player : null;
  const embed = meeting?.player?.kind === "embed" ? meeting.player : null;
  useEffect(() => {
    if (!fileRef.current || !file) return;
    return attachVideo(fileRef.current);
  }, [file?.src]); // eslint-disable-line react-hooks/exhaustive-deps

  useStoreEffect(meetingStore, (s) => Math.floor(s.currentTime), (t) => {
    if (timeRef.current) timeRef.current.textContent = `${clock(t)} / ${clock(duration)}`;
  });

  // Back to the video: scroll to it on the meeting page, or open the meeting page from anywhere else.
  const backToVideo = () => {
    if (here) { here.el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" }); return; }
    if (meeting) navigate(meetingHref(meeting.id));
  };
  const close = () => { setClosed(true); pause(); first.current = videoRef.current?.getBoundingClientRect() ?? null; setDocked(false); };

  // Drag the mini player anywhere; a press that doesn't move is a click (back to the video).
  const drag = useRef<{ id: number; dx: number; dy: number; x0: number; y0: number; moved: boolean } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (mode !== "mini" || e.button !== 0 || (e.target as HTMLElement).closest("button, [role=slider]")) return;
    const r = e.currentTarget.getBoundingClientRect();
    drag.current = { id: e.pointerId, dx: e.clientX - r.left, dy: e.clientY - r.top, x0: e.clientX, y0: e.clientY, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 4) return;
    d.moved = true;
    const p = clampPos({ x: e.clientX - d.dx, y: e.clientY - d.dy });
    Object.assign(e.currentTarget.style, { left: `${p.x}px`, top: `${p.y}px` });
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.id !== e.pointerId) return;
    if (d.moved) setPos(clampPos({ x: e.clientX - d.dx, y: e.clientY - d.dy }));
    else backToVideo();
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mode !== "mini" || e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); backToVideo(); return; }
    const step = e.shiftKey ? 40 : 10;
    const dir = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!dir) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    setPos(clampPos({ x: r.left + dir[0], y: r.top + dir[1] }));
  };

  if (!meeting?.vimeoId && !meeting?.player) return null;
  const recorded = meeting.status === "live" ? "Live" : "Recorded";
  return (
    <div
      ref={videoRef}
      className="video video--host"
      data-mode={mode}
      data-docked={mode === "mini" || undefined}
      style={meeting.video?.thumbnailUrl ? { backgroundImage: `url(${meeting.video.thumbnailUrl})` } : undefined}
      role={mode === "mini" ? "region" : undefined}
      aria-label={mode === "mini" ? "Mini player" : undefined}
      aria-hidden={mode === "hidden" || undefined}
      tabIndex={mode === "mini" ? 0 : undefined}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => { drag.current = null; }}
      onKeyDown={onKeyDown}
    >
      {file ? (
        <video ref={fileRef} className="video-frame" src={file.src} poster={file.poster} preload="metadata" playsInline aria-label={`${meeting.body} recording`} />
      ) : embed ? (
        <iframe className="video-frame video-frame--embed" title={`${meeting.body} recording`} src={embed.src} allow="autoplay; fullscreen; picture-in-picture" />
      ) : (
        <iframe
          ref={frameRef}
          className="video-frame"
          title={`${meeting.body} recording`}
          src={`https://player.vimeo.com/video/${meeting.vimeoId}?controls=0&title=0&byline=0&portrait=0&dnt=1`}
          allow="autoplay; fullscreen; picture-in-picture"
        />
      )}
      <div className="video-top">
        <span className="rec-dot" />
        {recorded}{meeting.location ? ` · ${meeting.location.split(",")[0]}` : ""}
      </div>
      {hidden && mode === "page" && (
        <button ref={showBtn} className="video-transcript-btn" onClick={() => {
          meetingStore.getState().setTranscriptHidden(false);
          meetingStore.getState().setFollowMode("following");
          dispatchEvent(new Event("transcript:live"));
        }}>
          <PanelRightOpen size={14} />Transcript
        </button>
      )}
      {(hidden || captions || mode === "mini") && <CaptionOverlay />}
      {mode === "mini" && (
        <>
          <div className="mini-tools">
            <button aria-label="Back to the video" onClick={(e) => { e.stopPropagation(); backToVideo(); }}><Maximize2 size={13} /></button>
            <button aria-label="Close mini player" onClick={(e) => { e.stopPropagation(); close(); }}><X size={13} /></button>
          </div>
          <MiniControls duration={duration} />
        </>
      )}
      {!embed && <div className="video-bottom">
        <SeekBar duration={duration} className="video-seek" label="Seek" />
        <div className="video-controls">
          <PlayButton />
          <span ref={timeRef}>{`${clock(0)} / ${clock(duration)}`}</span>
          <span className="spacer" />
          <button aria-label={captions ? "Hide captions" : "Show captions"} aria-pressed={captions || hidden} onClick={() => setCaptionsState(!captions)}>
            <Captions size={16} />
          </button>
          <button aria-label={muted ? "Unmute" : "Mute"} onClick={() => { setMuted(!muted); setMutedState(!muted); }}>
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button aria-label="Settings"><Settings size={16} /></button>
          <button aria-label="Full screen" onClick={requestFullscreen}><Maximize size={16} /></button>
        </div>
      </div>}
    </div>
  );
}
